import { useEffect, useState } from 'react';
import { checkoutSteps, FINCEN_PLACEMENT_AFTER_OFFER_ACCEPTED, FINCEN_PLACEMENT_AFTER_PLAID_CONNECTION } from './CheckoutWizard';
import { getCheckoutState } from 'merchant-api/checkout';
import { useApplication } from 'merchant-api/unified';
import { useDetailedOffers } from 'merchant-api/offer';
import { useOptimizelyVariation, PLAID_ADOPTION_FLAG, FINCEN_PLACEMENT_FLAG } from 'merchant-api/vendor/optimizely';
import { useAnalytics } from 'use-analytics';
import { useAtom } from 'jotai';
import { notNowClicked } from './Cards/PlaidLauncher/PlaidLauncherCard';

export const useCheckoutStep = (): {
  step?: keyof typeof checkoutSteps;
  stepLoading: boolean;
  stepError: boolean;
} => {
  const { applicationData, isErrorApplication, isLoadingApplication } = useApplication(['APPROVED']);

  const { data: plaidAdoptionVariation, isLoading: isLoadingPlaidAdoption } = useOptimizelyVariation({
    featureKey: PLAID_ADOPTION_FLAG,
    analytics: useAnalytics(),
  });

  const { data: fincenVariation, isLoading: isLoadingFincenVariation } = useOptimizelyVariation({
    featureKey: FINCEN_PLACEMENT_FLAG,
    analytics: useAnalytics(),
  });

  const { selectedOffer, isLoadingDetailedOffers } = useDetailedOffers();
  const { checkoutData, isErrorCheckoutState, isLoadingCheckoutState } = getCheckoutState();
  const [step, setStep] = useState<keyof typeof checkoutSteps>();
  const [notNow] = useAtom(notNowClicked);

  useEffect(() => {
    if (isLoadingCheckoutState || isLoadingApplication || isLoadingDetailedOffers || isLoadingPlaidAdoption || isLoadingFincenVariation || !checkoutData || !applicationData) {
      return;
    }

    if (checkoutData.checkoutStatus === 'COMPLETED') {
      if (plaidAdoptionVariation === 'plaid_flow' && !notNow && !(checkoutData.plaidOverride ?? false)) {
        setStep('PLAID_LAUNCHER');
        return;
      }

      setStep('CHECKOUT_SUCCESS');
      return;
    }

    if (applicationData.status === 'NOT_APPROVED') {
      throw new Error('Application not approved');
    }

    if (checkoutData.checkoutStatus !== 'INITIALIZED' && checkoutData.checkoutStatus !== 'UPDATED') {
      setStep('OFFER_REVIEW');
      return;
    }

    const fincenDone = !!checkoutData.isFincenAdditionalOwnersPassed && !!checkoutData.isFincenControlPersonPassed;

    let resolvedStep: keyof typeof checkoutSteps;

    switch (checkoutData.identityVerificationStatus) {
      case 'FAIL':
        resolvedStep = 'IDENTITY_FAILED';
        break;

      case 'NOT_STARTED': {
        const isPlaidFlow = plaidAdoptionVariation === 'plaid_flow';
        if (!checkoutData.routingNumber) {
          resolvedStep = 'BANK_VERIFICATION';
          break;
        }
        resolvedStep = isPlaidFlow || (checkoutData.plaidOverride ?? false) ? 'IDENTITY' : 'PLAID_LAUNCHER';
        break;
      }

      case 'CHALLENGE':
        resolvedStep = 'IDENTITY_CHALLENGE';
        break;

      default:
        if (selectedOffer?.product === 'LineOfCredit' && !checkoutData.selectedDrawAmount) {
          resolvedStep = 'REQUIRED_FIRST_DRAW';
          break;
        }

        resolvedStep = 'TERMS_AND_AGREEMENTS';
        break;
    }

    const isTerminalIdentityState = resolvedStep === 'IDENTITY_FAILED' || resolvedStep === 'IDENTITY_CHALLENGE';
    if (!fincenDone && !isTerminalIdentityState && fincenVariation === FINCEN_PLACEMENT_AFTER_OFFER_ACCEPTED) {
      setStep('FINCEN');
      return;
    }

    if (!fincenDone && fincenVariation === FINCEN_PLACEMENT_AFTER_PLAID_CONNECTION && resolvedStep === 'TERMS_AND_AGREEMENTS') {
      setStep('FINCEN');
      return;
    }

    setStep(resolvedStep);
  }, [
    applicationData,
    checkoutData,
    isLoadingApplication,
    isLoadingFincenVariation,
    isLoadingCheckoutState,
    isLoadingPlaidAdoption,
    isLoadingDetailedOffers,
    plaidAdoptionVariation,
    fincenVariation,
    selectedOffer,
    notNow,
  ]);

  return {
    step,
    stepLoading: step === undefined,
    stepError: isErrorApplication || isErrorCheckoutState,
  };
};
